import * as Yup from 'yup';
import {
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { RedesignChart } from '../../chart/RedesignChart';
import { useSelector } from 'react-redux';
import UserScreensInternetRanking from '../components/UserScreensInternetRanking';
import { getNiceTimestamp } from 'src/utils/get-nice-timestamp';
import DraftsIcon from '@mui/icons-material/Drafts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import MailIcon from '@mui/icons-material/Mail';

//Widget content - required
export const WidgetContent = ({ widgetData, editMode }) => {
  const { t } = useTranslation();
  const { notifications, allNotifications, loading } = useSelector(
    (state) => state.Notifications
  );
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedNotification(null);
  };

  if (loading) {
    return (
      <CardContent>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      </CardContent>
    );
  }

  return (
    <CardContent>
      {notifications && notifications.length > 0 ? (
        <List>
          {notifications.slice(0, 4).map((notification, index) => (
            <ListItem key={notification.id || index} disablePadding>
              <ListItemButton
                onClick={() => handleNotificationClick(notification)}
              >
                <ListItemIcon>
                    {notification.read ? (
                        <DraftsIcon/>
                    ) : (
                        <MailIcon />
                    )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    `${notification?.content.slice(0, 30)}${notification?.content.length > 30 ? '...' : ''}` ||
                    t('components.widgetNotifications.notification')
                  }
                  secondary={getNiceTimestamp(new Date(notification.date || notification.createdAt))}
                  primaryTypographyProps={{
                    fontWeight: notification.read ? 'normal' : 'bold',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography
          variant='body2'
          color='text.secondary'
          align='center'
          sx={{ py: 4 }}
        >
          {t('components.widgetNotifications.noNotifications')}
        </Typography>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {selectedNotification?.title ||
            t('components.widgetNotifications.notification')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedNotification?.message ||
              selectedNotification?.content ||
              t('components.widgetNotifications.noMessage')}
          </DialogContentText>
          {selectedNotification?.date && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ mt: 2, display: 'block' }}
            >
              {selectedNotification.date}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color='primary'>
            {t('components.widgetNotifications.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </CardContent>
  );
};

//Widget header - not required
export const WidgetHeader = ({ widgetData, actions, editMode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate()  
  return (
    <CardHeader
      title={(
        <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: "20px"
        }}>
        {t('components.widgetNotifications.yourNotifications')}
            <IconButton onClick={() => navigate('/notifications')}>
                <NotificationsIcon color="primary" />
            </IconButton>
        </Box>
      )}
      action={actions}
    />
  );
};

//Widget settings
export const type = 'notifications';
export const displayName = 'Your notifications';
export const configOptions = {};
